/**
 * The booth station, the scan: the tag, its preview, the age and emergency rows (split out of BoothStation.tsx
 * by interaction — James, Oct 21, item 5; no behavior change).
 */
import { useState, type ReactNode } from 'react';
import { MARK, MARK_COLOR } from '@cider/ui';
import { renderSVG } from 'uqr';
import { api, sentence } from '../../shell/api.js';
import { ON_THE_TAG, type Tag, first } from './BoothShared.js';

/** One printed tag (7d): the mark, the first name, the area, the time, the group, ISSUED, the QR. */
export function TagCard({ tag }: { tag: Tag }): ReactNode {
  return (
    <div className="booth__tag" data-testid="booth-tag">
      <img className="booth__tagmark" src={MARK_COLOR} alt="Apple Fest" />
      {/* The first name at the largest size that fits eleven letters across the tag (7d). */}
      <svg
        className="booth__tagname"
        viewBox={`0 0 ${String(Math.max(first(tag.name).length, 6) * 17)} 34`}
        role="img"
        aria-label={first(tag.name)}
      >
        <text x="0" y="28" fill="currentColor">
          {first(tag.name)}
        </text>
      </svg>
      <div>{tag.area}</div>
      {tag.group ? <div className="booth__tagissued">{tag.group}</div> : null}
      <div>
        {tag.day} · {tag.hours}
      </div>
      {tag.issued ? <div className="booth__tagissued">ISSUED · {tag.issued}</div> : null}
      {tag.code ? (
        <img
          className="booth__tagqr"
          data-testid="booth-tag-qr"
          src={`data:image/svg+xml;utf8,${encodeURIComponent(renderSVG(`CIDER-T:${tag.code}`))}`}
          alt="Scan to check out"
        />
      ) : null}
      {tag.code ? (
        <div className="booth__tagcode" data-testid="booth-tag-code">
          {tag.code}
        </div>
      ) : null}
    </div>
  );
}

/**
 * **The tag** (state 39, Print → Check-in tag): what is on it, and the tag drawn on screen in
 * both shapes until James picks one after the test print (TBD 115) — from this station's own last
 * check-ins, or a test tag when there are none. The red/black roll is not bought, so it is not
 * drawn here.
 */
export function TagPreview({ tags, year }: { tags: Tag[]; year: number | null }): ReactNode {
  const shown = tags.length ? tags : [{ name: 'Test tag', area: 'The booth', hours: '', day: '' }];
  const face = (tag: Tag, land: boolean, i: number): ReactNode => (
    <div
      key={`${land ? 'l' : 'p'}${String(i)}`}
      className={`booth__tagpv${land ? ' booth__tagpv--land' : ''}`}
      data-testid="booth-tagpv"
    >
      <div className="booth__tagpvtext">
        <div className="booth__tagpvtop">
          <img className="booth__tagpvmark" src={MARK} alt="Apple Fest" />
          {year ? <span className="booth__tagpvyear">{String(year)}</span> : null}
        </div>
        <div className="booth__tagpvname">{first(tag.name).toUpperCase()}</div>
        <div className="booth__tagpvarea">
          {tag.area.toUpperCase()}
          {land && tag.hours ? ` · ${tag.hours.toUpperCase()}` : ''}
        </div>
        {tag.group ? <div className="booth__fine">{tag.group}</div> : null}
        {!land && tag.hours ? <div className="booth__tagpvtime">{tag.hours}</div> : null}
        {tag.issued ? <div className="booth__tagpvissued">{tag.issued}</div> : null}
      </div>
      <div className="booth__tagpvqr">
        <img
          src={`data:image/svg+xml;utf8,${encodeURIComponent(renderSVG('CIDER-T:PREVIEW'))}`}
          alt="The check-out QR"
        />
        <span className="booth__fine">Scan at any booth to check out</span>
      </div>
    </div>
  );
  return (
    <div className="booth__found" data-testid="booth-tag-preview">
      <section className="booth__card">
        <div className="booth__cardhead">
          WHAT IS ON IT<span className="booth__cardsub">nothing else</span>
        </div>
        {ON_THE_TAG.map(([a, b, c]) => (
          <div className="booth__row" key={a}>
            <span className="booth__rowa">
              <b>{a}</b>
              {b ? <span className="booth__rowb">{b}</span> : null}
            </span>
            <span className="booth__rowc">{c}</span>
          </div>
        ))}
        <div className="booth__cardfoot">
          The Apple Fest mark, one color, on the plain black roll. The red/black roll is not bought.
        </div>
      </section>
      <section className="booth__card">
        <div className="booth__cardhead">
          THE TAG · QL-820NWB · 62 MM CONTINUOUS · CUT AT 100 MM · PORTRAIT AND LANDSCAPE UNTIL THE
          TEST PRINT (TBD 115)
        </div>
        <p className="booth__fine">
          Portrait and landscape until James picks after a test print. The QR checks the person out
          at any station.
        </p>
        <div className="booth__cardsub">PORTRAIT · 62 × 100 MM</div>
        <div className="booth__tagpvrow">{shown.map((t, i) => face(t, false, i))}</div>
        <div className="booth__cardsub">LANDSCAPE · 100 × 62 MM</div>
        <div className="booth__tagpvrow">{shown.map((t, i) => face(t, true, i))}</div>
      </section>
    </div>
  );
}

/**
 * **Who's at the desk** (7f): the operator finds their own name once; every action after takes
 * it. Signing in over somebody is "I'm taking over" — what they did stays in their name.
 */
/**
 * **Under 18?** (James, Oct 8 — the teen rule needs a fact): asked once at the desk when nobody
 * has. No is an answer; yes takes the year and month of birth, and the age is read from it.
 */
export function AgeRow({
  personId,
  onDone,
}: {
  personId: string;
  onDone: (says: string, age: number | null) => void;
}): ReactNode {
  const [yes, setYes] = useState(false);
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send = (under18: boolean): void => {
    setBusy(true);
    setRefusal(null);
    api<{ says: string; age: number | null }>('POST', '/volunteers/booth/birth', {
      personId,
      under18,
      ...(under18 ? { birthYear: Number(year), birthMonth: month ? Number(month) : null } : {}),
    })
      .then((r) => {
        onDone(r.says, r.age);
      })
      .catch((e: unknown) => {
        setRefusal(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <div className="booth__row booth__row--warn" data-testid="booth-age">
      <span className="booth__rowa">
        <b>UNDER 18?</b>
        <span className="booth__rowb">asked once — the teen rule reads the age</span>
      </span>
      <span className="booth__rowc">
        {yes ? (
          <>
            <input
              className="booth__input"
              aria-label="Year of birth"
              placeholder="Year"
              inputMode="numeric"
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
              }}
            />{' '}
            <input
              className="booth__input"
              aria-label="Month of birth"
              placeholder="Month (1 – 12)"
              inputMode="numeric"
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
              }}
            />
          </>
        ) : (
          'A working member aged 14 to 17 signs with a parent.'
        )}
        {refusal ? (
          <span className="booth__refusal" role="alert">
            {' '}
            {refusal}
          </span>
        ) : null}
      </span>
      {yes ? (
        <button
          type="button"
          className="booth__act"
          disabled={busy || !/^\d{4}$/.test(year)}
          onClick={() => {
            send(true);
          }}
        >
          Save
        </button>
      ) : (
        <>
          <button
            type="button"
            className="booth__act"
            disabled={busy}
            onClick={() => {
              send(false);
            }}
          >
            No
          </button>
          <button
            type="button"
            className="booth__act"
            onClick={() => {
              setYes(true);
            }}
          >
            Yes
          </button>
        </>
      )}
    </div>
  );
}

/**
 * **Emergency contact** (James, Oct 8 — a MISSING-row item for everyone): a name and a phone typed
 * at the desk, or — for an adult — asked and skipped, the skip in the record. Required under 18.
 */
export function EmergencyRow({
  personId,
  minor,
  onDone,
  onSkipped,
}: {
  personId: string;
  minor: boolean;
  onDone: (says: string) => void;
  onSkipped: (says: string) => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send = (
    path: string,
    body: Record<string, unknown>,
    then: (says: string) => void,
  ): void => {
    setBusy(true);
    setRefusal(null);
    api<{ says: string }>('POST', path, body)
      .then((r) => {
        then(r.says);
      })
      .catch((e: unknown) => {
        setRefusal(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <div className="booth__row booth__row--warn" data-testid="booth-emergency">
      <span className="booth__rowa">
        <b>EMERGENCY CONTACT</b>
        <span className="booth__rowb">
          {minor ? 'required under 18' : 'asked of everyone; an adult may skip it'}
        </span>
      </span>
      <span className="booth__rowc">
        <input
          className="booth__input"
          aria-label="Emergency contact’s name"
          placeholder="Their name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />{' '}
        <input
          className="booth__input"
          aria-label="Emergency contact’s phone"
          placeholder="Their phone"
          inputMode="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
          }}
        />
        {refusal ? (
          <span className="booth__refusal" role="alert">
            {' '}
            {refusal}
          </span>
        ) : null}
      </span>
      <button
        type="button"
        className="booth__act"
        disabled={busy || !name.trim() || !phone.trim()}
        onClick={() => {
          send('/volunteers/booth/emergency', { personId, name, phone }, onDone);
        }}
      >
        Save it
      </button>
      {minor ? null : (
        <button
          type="button"
          className="booth__act"
          disabled={busy}
          onClick={() => {
            send('/volunteers/booth/emergency/skip', { personId }, onSkipped);
          }}
        >
          Asked — skip
        </button>
      )}
    </div>
  );
}
